/**
 * ViaShapingDots.jsx — Single source of truth for rendering `via: true`
 * shaping waypoints on a Leaflet map (b99.9.41 · 2026-05-15).
 *
 * Background: Google Maps URLs encode TWO kinds of intermediate points:
 *   • Explicit stops (`!1m5!1m1!1s{pid}!2m2!1d!2d`) — places the user
 *     wants to visit (rendered as labelled pins).
 *   • Snap-to-road shaping hints (`!3m4!1m2!1d!2d`) — geometry hints
 *     that tell Google's routing engine "go via THIS point" without
 *     treating it as a stop.
 *
 * The Editor de Rotas has rendered the shaping hints as small white
 * dots on the polyline since b99.9.30. Trip Edit (RouteMapViewer)
 * was hiding them entirely (filter `!wp.isViaPoint`), which is what
 * the user spotted in the 2026-05-15 field bug:
 *
 *   "Continuam a não aparecer..." (anotando 3 círculos no mapa onde
 *   deviam estar os via points).
 *
 * Going forward: ANY map that renders Google-Maps-URL waypoints MUST
 * render the `via: true` ones using this component, so the
 * appearance is identical across the app.
 *
 * Props:
 *   • `waypoints` — array of `{ lat, lng, via }` (extra fields ignored).
 *     Only entries with `via === true` are drawn.
 *   • `color` — outer ring colour (default = canonical orange).
 *   • `radius` — dot radius in px (default 5, same as Editor).
 */
import { CircleMarker } from "react-leaflet";

import { ROUTE_COLOR, VIA_SHAPING } from "./mapIcons";

export default function ViaShapingDots({
  waypoints,
  color = ROUTE_COLOR,
  radius = VIA_SHAPING.radius,
}) {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return null;
  return waypoints
    .filter((wp) =>
      wp && (wp.via === true || wp.isViaPoint === true || wp.kind === "road") &&
      typeof wp.lat === "number" && typeof wp.lng === "number",
    )
    .map((wp, i) => (
      <CircleMarker
        key={`via-${i}-${wp.lat.toFixed(5)}-${wp.lng.toFixed(5)}`}
        center={[wp.lat, wp.lng]}
        radius={radius}
        pathOptions={{ ...VIA_SHAPING, color, radius }}
        interactive={false}
      />
    ));
}
